/**
 * Página de configuração visual do chat público do fluxo
 * Design premium inspirado no Typebot com painéis flutuantes
 * Suporte a 8 templates pré-configurados (WhatsApp, Instagram, TikTok, etc.)
 * 
 * RECURSOS VISUAIS SUPORTADOS:
 * - Mensagens de texto
 * - Imagens com preview
 * - Vídeos com player
 * - Áudios com waveform
 * - Documentos com ícones dinâmicos
 * - Botões interativos
 * - Listas de opções
 * - Links com preview
 */

import { useParams, useNavigate } from "react-router-dom";
import logoUaizyIcon from "@/assets/logo-uaizy-icon.png";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useFlowChatConfig, FlowChatConfig } from "@/hooks/useFlowChatConfig";
import { FlowBuilderTabs } from "@/components/tenant/flows/FlowBuilderTabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MediaUrlInput } from "@/components/ui/media-url-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Save,
  RotateCcw,
  Bot,
  User,
  MessageSquare,
  Building2,
  Palette,
  LayoutTemplate,
  ChevronDown,
  ChevronUp,
  Loader2,
  Code,
  Maximize2,
  Smartphone,
  Instagram,
  Send,
  MessageCircle,
  Hash,
  Zap,
  Check,
  Video,
  Phone,
  MoreVertical,
  Smile,
  Paperclip,
  Camera,
  Mic,
  ChevronRight,
} from "lucide-react";
import { useState, useEffect } from "react";
import { showToast } from "@/lib/toast";
import { cn } from "@/lib/utils";

// Importação dos componentes de mensagem para recursos visuais
import { ImageMessageBubble } from "@/components/tenant/chat/ImageMessageBubble";
import { VideoMessageBubble } from "@/components/tenant/chat/VideoMessageBubble";
import { AudioMessageBubble } from "@/components/tenant/chat/AudioMessageBubble";
import { DocumentMessageBubble } from "@/components/tenant/chat/DocumentMessageBubble";
import { ButtonListMessageBubble } from "@/components/tenant/chat/ButtonListMessageBubble";
import { OptionListMessageBubble } from "@/components/tenant/chat/OptionListMessageBubble";
import { LinkMessageBubble } from "@/components/tenant/chat/LinkMessageBubble";
import { AudioPlayerProvider } from "@/contexts/AudioPlayerContext";

// ============================================================================
// DEFINIÇÃO DOS TEMPLATES DE CHAT
// 8 templates pré-configurados para diferentes plataformas
// ============================================================================
// Interface para templates de chat
interface ChatTemplate {
  id: string;
  name: string;
  icon: React.ReactNode;
  popular?: boolean;
  config: {
    primary_color: string;
    background_color: string;
    header_bg_color: string;
    header_text_color: string;
    user_bubble_color: string;
    bot_bubble_color: string;
    user_text_color: string;
    bot_text_color: string;
    border_radius: string;
    background_image: string | null;
  };
}

const CHAT_TEMPLATES: ChatTemplate[] = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: <Smartphone className="h-5 w-5" />,
    popular: true,
    config: {
      primary_color: '#075E54', // Padronizado com header_bg_color
      background_color: '#E5DDD5',
      header_bg_color: '#075E54',
      header_text_color: '#ffffff',
      user_bubble_color: '#DCF8C6',
      bot_bubble_color: '#ffffff',
      user_text_color: '#000000',
      bot_text_color: '#000000',
      border_radius: '8px',
      background_image: `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/anexos/system/background-whatsapp.png`,
    },
  },
  {
    id: 'instagram',
    name: 'Instagram',
    icon: <Instagram className="h-5 w-5" />,
    popular: true,
    config: {
      primary_color: '#E1306C',
      background_color: '#FAFAFA',
      header_bg_color: '#ffffff',
      header_text_color: '#262626',
      user_bubble_color: '#3797F0',
      bot_bubble_color: '#EFEFEF',
      user_text_color: '#ffffff',
      bot_text_color: '#000000',
      border_radius: '22px',
      background_image: null,
    },
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    icon: <Zap className="h-5 w-5" />,
    config: {
      primary_color: '#FF0050',
      background_color: '#121212',
      header_bg_color: '#000000',
      header_text_color: '#ffffff',
      user_bubble_color: '#FE2C55',
      bot_bubble_color: '#2F2F2F',
      user_text_color: '#ffffff',
      bot_text_color: '#ffffff',
      border_radius: '16px',
      background_image: null,
    },
  },
  {
    id: 'messenger',
    name: 'Messenger',
    icon: <MessageCircle className="h-5 w-5" />,
    config: {
      primary_color: '#0084FF',
      background_color: '#ffffff',
      header_bg_color: '#ffffff',
      header_text_color: '#050505',
      user_bubble_color: '#0084FF',
      bot_bubble_color: '#E4E6EB',
      user_text_color: '#ffffff',
      bot_text_color: '#050505',
      border_radius: '18px',
      background_image: null,
    },
  },
  {
    id: 'telegram',
    name: 'Telegram',
    icon: <Send className="h-5 w-5" />,
    config: {
      primary_color: '#0088CC',
      background_color: '#D6E6F2',
      header_bg_color: '#517DA2',
      header_text_color: '#ffffff',
      user_bubble_color: '#EFFDDE',
      bot_bubble_color: '#ffffff',
      user_text_color: '#000000',
      bot_text_color: '#000000',
      border_radius: '12px',
      background_image: null,
    },
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: <Hash className="h-5 w-5" />,
    config: {
      primary_color: '#5865F2',
      background_color: '#313338',
      header_bg_color: '#2B2D31',
      header_text_color: '#ffffff',
      user_bubble_color: '#5865F2',
      bot_bubble_color: '#2B2D31',
      user_text_color: '#ffffff',
      bot_text_color: '#DBDEE1',
      border_radius: '4px',
      background_image: null,
    },
  },
  {
    id: 'slack',
    name: 'Slack',
    icon: <MessageSquare className="h-5 w-5" />,
    config: {
      primary_color: '#4A154B',
      background_color: '#F4EDE4',
      header_bg_color: '#4A154B',
      header_text_color: '#ffffff',
      user_bubble_color: '#007A5A',
      bot_bubble_color: '#ffffff',
      user_text_color: '#ffffff',
      bot_text_color: '#1D1C1D',
      border_radius: '6px',
      background_image: null,
    },
  },
  {
    id: 'uaizychat',
    name: 'UaizyChat',
    icon: <img src={logoUaizyIcon} alt="UaizyChat" className="h-5 w-5 object-contain" />,
    config: {
      primary_color: '#5BBFBA',
      background_color: '#ffffff',
      header_bg_color: '#5BBFBA',
      header_text_color: '#ffffff',
      user_bubble_color: '#5BBFBA',
      bot_bubble_color: '#FFFFFF',
      user_text_color: '#ffffff',
      bot_text_color: '#1F2937',
      border_radius: '16px',
      background_image: null,
    },
  },
];

// ============================================================================
// COMPONENTE: TemplateCard
// Card visual de template com mini-preview do chat
// ============================================================================
interface TemplateCardProps {
  template: ChatTemplate;
  isSelected: boolean;
  onClick: () => void;
}

const TemplateCard = ({ template, isSelected, onClick }: TemplateCardProps) => {
  const { config } = template;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative p-3 rounded-xl border-2 transition-all duration-200 text-left group hover:shadow-md",
        isSelected 
          ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20" 
          : "border-border hover:border-orange-300"
      )}
    >
      {/* Badge de selecionado */}
      {isSelected && (
        <div className="absolute -top-2 -right-2 bg-orange-500 rounded-full p-1">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
      
      {/* Badge popular */}
      {template.popular && (
        <Badge 
          variant="secondary" 
          className="absolute -top-2 left-2 text-[10px] px-1.5 py-0 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
        >
          Popular
        </Badge>
      )}
      
      {/* Mini-preview do chat */}
      <div 
        className="w-full aspect-[4/3] rounded-lg overflow-hidden mb-2 shadow-sm"
        style={{ backgroundColor: config.background_color }}
      >
        {/* Mini header */}
        <div 
          className="h-5 flex items-center px-2"
          style={{ backgroundColor: config.header_bg_color }}
        >
          <div className="w-2 h-2 rounded-full bg-white/50" />
          <div className="ml-1.5 w-8 h-1.5 rounded-full bg-white/50" />
        </div>
        
        {/* Mini mensagens */}
        <div className="p-1.5 space-y-1">
          {/* Mensagem bot */}
          <div className="flex items-end gap-1">
            <div 
              className="w-14 h-3 rounded-full"
              style={{ 
                backgroundColor: config.bot_bubble_color,
                borderRadius: config.border_radius,
              }}
            />
          </div>
          
          {/* Mensagem usuário */}
          <div className="flex justify-end">
            <div 
              className="w-12 h-3 rounded-full"
              style={{ 
                backgroundColor: config.user_bubble_color,
                borderRadius: config.border_radius,
              }}
            />
          </div>
          
          {/* Mensagem bot */}
          <div className="flex items-end gap-1">
            <div 
              className="w-16 h-3 rounded-full"
              style={{ 
                backgroundColor: config.bot_bubble_color,
                borderRadius: config.border_radius,
              }}
            />
          </div>
        </div>
      </div>
      
      {/* Nome e ícone */}
      <div className="flex items-center gap-2">
        <div 
          className="p-1.5 rounded-md"
          style={{ backgroundColor: config.primary_color + '20' }}
        >
          <span style={{ color: config.primary_color }}>
            {template.icon}
          </span>
        </div>
        <span className="text-sm font-medium">{template.name}</span>
      </div>
    </button>
  );
};

// ============================================================================
// COMPONENTE: ChatPreview
// Preview ao vivo do chat com as configurações aplicadas
// Renderização condicional para template WhatsApp (header/input autênticos)
// ============================================================================
interface ChatPreviewProps {
  config: Partial<FlowChatConfig>;
}

// ============================================================================
// INTERFACE: PreviewMessage
// Suporte a todos os tipos de mídia do Flow Engine
// ============================================================================
interface PreviewMessage {
  type: 'bot' | 'user';
  text?: string;
  // Timestamp da mensagem para exibição do horário
  timestamp?: Date;
  // Tipo de mídia para renderização condicional
  mediaType?: 'image' | 'video' | 'audio' | 'document' | 'buttons' | 'list' | 'link';
  mediaUrl?: string;
  // Props específicas por tipo
  caption?: string;
  duration?: number;
  filename?: string;
  mimeType?: string;
  buttons?: Array<{ id: string; label: string }>;
  listOptions?: Array<{ id: string; label: string; description?: string }>;
  listTitle?: string;
  linkTitle?: string;
  linkDescription?: string;
  linkThumbnailUrl?: string;
}

const ChatPreview = ({ config }: ChatPreviewProps) => {
  // Estado para simular digitação no preview (alterna a cada 3s)
  const [isTyping, setIsTyping] = useState(true);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTyping(prev => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Mensagens de exemplo para preview (renderização 100% local, sem tráfego externo)
  const previewMessages: PreviewMessage[] = [
    // Mensagem de texto do bot
    { type: 'bot', text: 'Olá! 👋 Como posso ajudá-lo hoje?', timestamp: new Date() },
    
    // Resposta do usuário
    { type: 'user', text: 'Gostaria de saber mais sobre seus serviços', timestamp: new Date() },
    
    // Bot faz uma pergunta com botões (estilo WhatsApp)
    { 
      type: 'bot', 
      text: 'Qual das opções precisa de atendimento?',
      mediaType: 'buttons',
      buttons: [
        { id: '1', label: 'Comercial' },
        { id: '2', label: 'Financeiro' },
        { id: '3', label: 'Suporte Técnico' },
      ],
      timestamp: new Date()
    },
  ];

  /**
   * Renderiza o conteúdo da mensagem baseado no tipo de mídia
   * Usa os componentes existentes do sistema de chat
   */
  const renderMessageContent = (msg: PreviewMessage) => {
    const direction = msg.type === 'user' ? 'outbound' : 'inbound';
    
    switch (msg.mediaType) {
      case 'image':
        return (
          <ImageMessageBubble
            url={msg.mediaUrl!}
            caption={msg.caption}
            direction={direction}
          />
        );
      
      case 'video':
        return (
          <VideoMessageBubble
            url={msg.mediaUrl!}
            caption={msg.caption}
            duration={msg.duration}
            direction={direction}
          />
        );
      
      case 'audio':
        return (
          <AudioMessageBubble
            url={msg.mediaUrl!}
            duration={msg.duration}
            isPTT={true}
            direction={direction}
          />
        );
      
      case 'document':
        return (
          <DocumentMessageBubble
            url={msg.mediaUrl!}
            filename={msg.filename}
            mimeType={msg.mimeType}
            direction={direction}
          />
        );
      
      case 'buttons':
        return (
          <ButtonListMessageBubble
            content={msg.text || ''}
            options={msg.buttons || []}
            direction={direction}
            variant={isWhatsApp ? 'whatsapp' : 'default'}
          />
        );
      
      case 'list':
        return (
          <OptionListMessageBubble
            content={msg.text || ''}
            title={msg.listTitle}
            options={msg.listOptions || []}
            direction={direction}
          />
        );
      
      case 'link':
        return (
          <LinkMessageBubble
            url={msg.mediaUrl!}
            title={msg.linkTitle}
            description={msg.linkDescription}
            thumbnailUrl={msg.linkThumbnailUrl}
            direction={direction}
          />
        );
      
      default:
        // Mensagem de texto padrão
        return msg.text;
    }
  };

  // Extrair cores do config com fallbacks
  const headerBg = config.header_bg_color || config.primary_color || '#3b82f6';
  const headerText = config.header_text_color || '#ffffff';
  const userBubble = config.user_bubble_color || config.primary_color || '#3b82f6';
  const botBubble = config.bot_bubble_color || '#f3f4f6';
  const userText = config.user_text_color || '#ffffff';
  const botText = config.bot_text_color || '#1f2937';
  const borderRadius = config.border_radius || '16px';
  
  // Identificar template atual
  const isWhatsApp = config.template_id === 'whatsapp';
  const isInstagram = config.template_id === 'instagram';
  const isMessenger = config.template_id === 'messenger';
  const isTikTok = config.template_id === 'tiktok';
  const isTelegram = config.template_id === 'telegram';
  
  // Cor primária efetiva: usa header_bg_color como fallback para WhatsApp para manter consistência visual
  const effectivePrimaryColor = config.primary_color || (isWhatsApp ? config.header_bg_color : '#5BBFBA');

  // Texto de digitação por plataforma
  const getTypingText = () => {
    if (isWhatsApp) return 'digitando...';
    if (isInstagram) return 'Digitando...';
    if (isMessenger) return 'Digitando...';
    if (isTikTok) return 'Digitando...';
    if (isTelegram) return 'digitando...';
    return 'Digitando...';
  };

  // Cor dos pontos de digitação por plataforma
  const getTypingDotColor = () => {
    if (isWhatsApp) return '#667781';
    if (isInstagram) return '#8e8e8e';
    if (isMessenger) return '#0084ff';
    if (isTikTok) return '#fe2c55';
    if (isTelegram) return '#0088cc';
    return '#9ca3af';
  };

  return (
    <AudioPlayerProvider>
    <div 
      className="w-full h-full flex items-center justify-center p-8 transition-colors duration-300"
      style={{ backgroundColor: config.background_color || '#f3f4f6' }}
    >
      {/* CSS para animação dos pontos de digitação */}
      {/* Animação de digitação e estilos das setinhas WhatsApp */}
      <style>
        {`
          @keyframes typingBounce {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-4px);
              opacity: 1;
            }
          }
          
          /* Setinha/tail das bolhas WhatsApp - lado esquerdo (bot) */
          .whatsapp-tail-left::before {
            content: '';
            position: absolute;
            left: -8px;
            bottom: 0;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 0 10px 10px;
            border-color: transparent transparent #ffffff transparent;
          }
          
          /* Setinha/tail das bolhas WhatsApp - lado direito (user) */
          .whatsapp-tail-right::after {
            content: '';
            position: absolute;
            right: -8px;
            bottom: 0;
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 0 10px 10px 0;
            border-color: transparent #DCF8C6 transparent transparent;
          }
        `}
      </style>
      
      {/* CSS customizado do usuário - injetado dinamicamente */}
      {config.custom_css && (
        <style>{config.custom_css}</style>
      )}

      {/* Container do chat com max-width configurável */}
      <div 
        className="w-full h-full max-h-[600px] rounded-2xl shadow-xl overflow-hidden flex flex-col border transition-all duration-300"
        style={{ 
          maxWidth: config.max_width || '450px',
          borderRadius: isWhatsApp ? '0px' : borderRadius,
        }}
      >
        {/* ========== HEADER ========== */}
        {isWhatsApp ? (
          // Header estilo WhatsApp autêntico
          <div 
            className="flex items-center gap-2 px-3 py-2 transition-colors duration-300"
            style={{ backgroundColor: headerBg }}
          >
            {/* Seta voltar */}
            <ArrowLeft className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
            
            {/* Avatar circular - só mostra se bot_avatar_enabled */}
            {config.bot_avatar_enabled && (
              <Avatar className="h-10 w-10 border-2 border-white/20">
                {config.bot_avatar_url ? (
                  <AvatarImage src={config.bot_avatar_url} />
                ) : config.company_logo_url ? (
                  <AvatarImage src={config.company_logo_url} />
                ) : null}
                <AvatarFallback 
                  style={{ 
                    backgroundColor: `${effectivePrimaryColor}20`,
                  }}
                >
                  <Bot className="h-5 w-5" style={{ color: effectivePrimaryColor }} />
                </AvatarFallback>
              </Avatar>
            )}
            
            {/* Nome + status online/digitando */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm truncate" style={{ color: headerText }}>
                {config.company_name || 'Assistente Virtual'}
              </h3>
              <p className="text-xs" style={{ color: headerText, opacity: 0.85 }}>
                {isTyping ? getTypingText() : 'online'}
              </p>
            </div>
            
            {/* Ícones direita (vídeo, telefone, menu) */}
            <div className="flex items-center gap-4">
              <Video className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
              <Phone className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
              <MoreVertical className="h-5 w-5 cursor-pointer" style={{ color: headerText }} />
            </div>
          </div>
        ) : (
          // Header padrão para outros templates
          <div 
            className="p-4 flex items-center gap-3 border-b transition-colors duration-300"
            style={{ backgroundColor: headerBg }}
          >
            {config.company_logo_url ? (
              <img 
                src={config.company_logo_url} 
                alt="Logo" 
                className="h-10 w-10 rounded-full object-cover bg-white/20"
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                <Building2 className="h-5 w-5" style={{ color: headerText }} />
              </div>
            )}
            <div className="flex-1">
              <h3 className="font-semibold" style={{ color: headerText }}>
                {config.company_name || 'Empresa'}
              </h3>
              <p className="text-xs" style={{ color: headerText, opacity: 0.8 }}>
                {isTyping ? getTypingText() : 'Online agora'}
              </p>
            </div>
          </div>
        )}

        {/* ========== ÁREA DE MENSAGENS ========== */}
        <div 
          className="flex-1 p-4 space-y-4 overflow-y-auto transition-colors duration-300"
          style={{ 
            backgroundColor: config.background_color || '#f3f4f6',
            // Imagem de fundo quando configurada (WhatsApp)
            ...(config.background_image && {
              backgroundImage: `url(${config.background_image})`,
              backgroundRepeat: 'repeat',
              backgroundSize: 'auto',
            }),
          }}
        >
          {previewMessages.map((msg, idx) => {
            // Para WhatsApp com buttons/list, o componente já tem seu próprio container
            const isSpecialBubble = isWhatsApp && (msg.mediaType === 'buttons' || msg.mediaType === 'list');
            
            return (
            <div
              key={idx}
              className={`flex items-end gap-2 ${
                msg.type === 'user' ? 'justify-end' : 'justify-start'
              }`}
            >
              {/* Avatar do bot - usa primary_color para consistência com botão */}
              {msg.type === 'bot' && config.bot_avatar_enabled && (
                <Avatar className="h-8 w-8">
                  {config.bot_avatar_url ? (
                    <AvatarImage src={config.bot_avatar_url} />
                  ) : null}
                  <AvatarFallback 
                    style={{ 
                      backgroundColor: `${effectivePrimaryColor}20`,
                    }}
                  >
                    <Bot className="h-4 w-4" style={{ color: effectivePrimaryColor }} />
                  </AvatarFallback>
                </Avatar>
              )}
              
              {/* Bolha da mensagem - WhatsApp buttons/list têm container próprio */}
              {isSpecialBubble ? (
                <div className={cn(
                  "max-w-[75%] relative",
                  isWhatsApp && msg.type === 'bot' && 'whatsapp-tail-left'
                )}>
                  {renderMessageContent(msg)}
                </div>
              ) : (
                <div
                  className={cn(
                    "max-w-[75%] px-4 py-2.5 text-sm transition-all duration-300 shadow-sm relative",
                    isWhatsApp && msg.type === 'bot' && 'whatsapp-tail-left',
                    isWhatsApp && msg.type === 'user' && 'whatsapp-tail-right'
                  )}
                  style={{
                    backgroundColor: msg.type === 'user' ? userBubble : botBubble,
                    color: msg.type === 'user' ? userText : botText,
                    borderRadius: borderRadius,
                    borderBottomRightRadius: msg.type === 'user' ? '4px' : borderRadius,
                    borderBottomLeftRadius: msg.type === 'bot' ? '4px' : borderRadius,
                    // Padding extra para acomodar timestamp e check nas mensagens do usuário (WhatsApp)
                    paddingBottom: isWhatsApp && msg.type === 'user' ? '18px' : undefined,
                  }}
                >
                  {renderMessageContent(msg)}
                  
                  {/* Indicador de leitura (timestamp + ✓✓) para mensagens do usuário no WhatsApp */}
                  {isWhatsApp && msg.type === 'user' && (
                    <span className="absolute bottom-1 right-2 flex items-center gap-0.5">
                      <span className="text-[10px] text-gray-500 mr-1">
                        {msg.timestamp?.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) || '12:00'}
                      </span>
                      {/* Duplo check verde - simulando "lida" */}
                      <svg 
                        viewBox="0 0 16 11" 
                        height="11" 
                        width="16" 
                        className="text-[#53bdeb]"
                        fill="currentColor"
                      >
                        <path d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.405-2.272a.463.463 0 0 0-.336-.146.47.47 0 0 0-.343.146l-.311.31a.445.445 0 0 0-.14.337c0 .136.047.25.14.343l2.996 2.996a.724.724 0 0 0 .241.178.732.732 0 0 0 .298.064.727.727 0 0 0 .559-.267l6.664-8.253a.46.46 0 0 0 .102-.355.493.493 0 0 0-.178-.381l-.31-.305z" />
                        <path d="M14.757.653a.457.457 0 0 0-.305-.102.493.493 0 0 0-.38.178l-6.19 7.636-1.156-1.093-.31.31 1.689 1.689a.724.724 0 0 0 .241.178.732.732 0 0 0 .298.064.727.727 0 0 0 .559-.267l6.664-8.253a.46.46 0 0 0 .102-.355.493.493 0 0 0-.178-.381l-.31-.305z" />
                      </svg>
                    </span>
                  )}
                </div>
              )}

              {/* Avatar do usuário (não mostrar no WhatsApp) */}
              {msg.type === 'user' && config.user_avatar_enabled && !isWhatsApp && (
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-muted">
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
            );
          })}

          {/* Input flutuante dentro do chat - Estilo Typebot */}
          {/* Em desktop: ocupa metade à direita, em mobile: tela inteira */}
          <div className="flex justify-end mt-4">
            <div className="flex items-center gap-3 w-full md:w-1/2">
              <input
                placeholder="Digite sua resposta..."
                disabled
                className="flex-1 px-5 py-3 text-sm bg-white border shadow-sm outline-none"
                style={{
                  borderRadius: '9999px',
                  borderColor: '#E5E7EB',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
              />
              <button
                disabled
                className="shrink-0 px-6 py-3 text-sm font-semibold text-white shadow-sm cursor-default"
                style={{
                  backgroundColor: effectivePrimaryColor,
                  borderRadius: '9999px',
                  height: '48px',
                  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                }}
              >
                Enviar
              </button>
            </div>
          </div>
        </div>
        
        {/* Branding */}
        {config.show_branding && (
          <p className="text-[10px] text-muted-foreground text-center py-2 bg-transparent">
            Powered by UaizyChat
          </p>
        )}
      </div>
    </div>
    </AudioPlayerProvider>
  );
};

// ============================================================================
// COMPONENTE: NumberInputWithUnit
// Input numérico com spinner e dropdown de unidade
// ============================================================================
interface NumberInputWithUnitProps {
  value: string;
  onChange: (value: string) => void;
  units?: string[];
  defaultUnit?: string;
}

const NumberInputWithUnit = ({ 
  value, 
  onChange, 
  units = ['px', '%', 'vh'],
  defaultUnit = 'px'
}: NumberInputWithUnitProps) => {
  // Extrair número e unidade do valor
  const match = value?.match(/^(\d+)(\D+)?$/);
  const numValue = match ? parseInt(match[1]) : 450;
  const unitValue = match?.[2] || defaultUnit;

  const handleNumberChange = (newNum: number) => {
    if (newNum >= 0) {
      onChange(`${newNum}${unitValue}`);
    }
  };

  const handleUnitChange = (newUnit: string) => {
    onChange(`${numValue}${newUnit}`);
  };

  return (
    <div className="flex items-center gap-1">
      {/* Campo numérico com botões de incremento/decremento */}
      <div className="flex items-center border rounded-md bg-background">
        <Input
          type="number"
          value={numValue}
          onChange={(e) => handleNumberChange(parseInt(e.target.value) || 0)}
          className="w-20 border-0 text-right pr-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <div className="flex flex-col border-l">
          <button
            type="button"
            onClick={() => handleNumberChange(numValue + 10)}
            className="px-1.5 py-0.5 hover:bg-muted transition-colors"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => handleNumberChange(Math.max(0, numValue - 10))}
            className="px-1.5 py-0.5 hover:bg-muted transition-colors border-t"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
      
      {/* Dropdown de unidade */}
      <Select value={unitValue} onValueChange={handleUnitChange}>
        <SelectTrigger className="w-16 h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {units.map((unit) => (
            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// ============================================================================
// COMPONENTE: ColorPickerField
// Campo de cor com preview e input
// ============================================================================
interface ColorPickerFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  showToggle?: boolean;
  toggleValue?: boolean;
  onToggleChange?: (value: boolean) => void;
}

const ColorPickerField = ({ 
  label, 
  value, 
  onChange,
  showToggle,
  toggleValue,
  onToggleChange
}: ColorPickerFieldProps) => {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        {showToggle && (
          <Switch
            checked={toggleValue}
            onCheckedChange={onToggleChange}
            className="data-[state=checked]:bg-orange-500"
          />
        )}
      </div>
      <div className="flex items-center gap-2">
        <div 
          className="w-8 h-8 rounded-md border cursor-pointer"
          style={{ backgroundColor: value }}
        />
        <Input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-8 p-0.5 cursor-pointer"
        />
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: FlowTheme
// ============================================================================
export default function FlowTheme() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  
  // Estado para controlar subseções colapsáveis
  const [borderOpen, setBorderOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [colorsOpen, setColorsOpen] = useState(false);

  // Hook para gerenciar configurações do chat
  const {
    config,
    isLoading,
    isSaving,
    updateField,
    saveConfig,
    resetToDefaults,
  } = useFlowChatConfig(flowId || '');

  // Buscar nome do fluxo para exibição
  const { data: flowName } = useQuery({
    queryKey: ['flow-name', flowId],
    queryFn: async () => {
      if (!flowId) return 'Fluxo';
      const { data } = await supabase
        .from('flows')
        .select('name')
        .eq('id', flowId)
        .single();
      return data?.name || 'Fluxo';
    },
    enabled: !!flowId,
  });

  // Função para aplicar um template completo
  const applyTemplate = (templateId: string) => {
    const template = CHAT_TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    // Aplicar todas as configurações do template de uma vez
    updateField('template_id', templateId);
    updateField('primary_color', template.config.primary_color);
    updateField('background_color', template.config.background_color);
    updateField('header_bg_color', template.config.header_bg_color);
    updateField('header_text_color', template.config.header_text_color);
    updateField('user_bubble_color', template.config.user_bubble_color);
    updateField('bot_bubble_color', template.config.bot_bubble_color);
    updateField('user_text_color', template.config.user_text_color);
    updateField('bot_text_color', template.config.bot_text_color);
    updateField('border_radius', template.config.border_radius);
    updateField('background_image', template.config.background_image);

    showToast.success(`Template ${template.name} aplicado!`);
  };

  // Handler para salvar configurações
  const handleSave = async () => {
    try {
      await saveConfig();
      showToast.success('Configurações salvas com sucesso!');
    } catch (error) {
      showToast.error('Erro ao salvar configurações');
    }
  };

  // Aguarda carregamento e inicialização do config
  if (isLoading || !config) {
    return (
      <div className="h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-muted/50">
      {/* Header com navegação e ações */}
      <header className="bg-card border-b px-4 py-2 relative flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/tenant/flows')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="font-medium truncate max-w-[200px]">{flowName}</span>
        </div>

        {/* Tabs de navegação - posicionamento absoluto central */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <FlowBuilderTabs />
        </div>

        {/* Ações */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="xs"
            onClick={resetToDefaults}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            Resetar
          </Button>
          <Button
            size="xs"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Salvar
          </Button>
        </div>
      </header>

      {/* Conteúdo principal com painéis flutuantes */}
      <div className="flex-1 flex gap-4 p-4 overflow-hidden">
        {/* Menu lateral flutuante */}
        <div className="w-[380px] bg-card rounded-xl shadow-sm overflow-hidden shrink-0 flex flex-col">
          <ScrollArea className="flex-1">
            <Accordion 
              type="multiple" 
              defaultValue={["templates", "chat"]}
              className="w-full"
            >
              {/* ========== SEÇÃO: Templates ========== */}
              <AccordionItem value="templates" className="border-b">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Templates</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {/* Grid de templates 2x4 */}
                  <div className="grid grid-cols-2 gap-3">
                    {CHAT_TEMPLATES.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        isSelected={config.template_id === template.id}
                        onClick={() => applyTemplate(template.id)}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ========== SEÇÃO: Global ========== */}
              <AccordionItem value="global" className="border-b">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Palette className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Global</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  <ColorPickerField
                    label="Cor principal:"
                    value={config.primary_color || '#f97316'}
                    onChange={(v) => {
                      updateField('primary_color', v);
                      updateField('template_id', null); // Marca como customizado
                    }}
                  />
                  <ColorPickerField
                    label="Fundo:"
                    value={config.background_color || '#ffffff'}
                    onChange={(v) => {
                      updateField('background_color', v);
                      updateField('template_id', null);
                    }}
                  />

                  {/* Imagem de fundo - com seleção via galeria de mídia */}
                  <MediaUrlInput
                    value={config.background_image || null}
                    onChange={(url) => {
                      updateField('background_image', url);
                      updateField('template_id', null);
                    }}
                    label="Imagem de fundo (URL)"
                    placeholder="https://exemplo.com/fundo.png"
                    showPreview={true}
                    allowedTypes={['image']}
                  />
                  
                  {/* Subseção: Cores Avançadas */}
                  <Collapsible open={colorsOpen} onOpenChange={setColorsOpen}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                      <span>Cores Avançadas</span>
                      {colorsOpen ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-3 pt-2">
                      <ColorPickerField
                        label="Header fundo:"
                        value={config.header_bg_color || '#f97316'}
                        onChange={(v) => {
                          updateField('header_bg_color', v);
                          updateField('template_id', null);
                        }}
                      />
                      <ColorPickerField
                        label="Header texto:"
                        value={config.header_text_color || '#ffffff'}
                        onChange={(v) => {
                          updateField('header_text_color', v);
                          updateField('template_id', null);
                        }}
                      />
                      <ColorPickerField
                        label="Bolha usuário:"
                        value={config.user_bubble_color || '#f97316'}
                        onChange={(v) => {
                          updateField('user_bubble_color', v);
                          updateField('template_id', null);
                        }}
                      />
                      <ColorPickerField
                        label="Bolha bot:"
                        value={config.bot_bubble_color || '#f3f4f6'}
                        onChange={(v) => {
                          updateField('bot_bubble_color', v);
                          updateField('template_id', null);
                        }}
                      />
                      <ColorPickerField
                        label="Texto usuário:"
                        value={config.user_text_color || '#ffffff'}
                        onChange={(v) => {
                          updateField('user_text_color', v);
                          updateField('template_id', null);
                        }}
                      />
                      <ColorPickerField
                        label="Texto bot:"
                        value={config.bot_text_color || '#1f2937'}
                        onChange={(v) => {
                          updateField('bot_text_color', v);
                          updateField('template_id', null);
                        }}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                </AccordionContent>
              </AccordionItem>

              {/* ========== SEÇÃO: Chat (principal, borda laranja) ========== */}
              <AccordionItem 
                value="chat" 
                className="border-b border-l-[3px] border-l-orange-500"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-4 w-4 text-orange-500" />
                    <span className="font-medium">Chat</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-6">
                  {/* Container */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Container
                    </h4>
                    
                    {/* Max width */}
                    <div className="flex items-center justify-between">
                      <Label className="text-sm text-muted-foreground">Max width:</Label>
                      <NumberInputWithUnit
                        value={config.max_width || '450px'}
                        onChange={(v) => updateField('max_width', v)}
                      />
                    </div>

                    {/* Subseção: Border */}
                    <Collapsible open={borderOpen} onOpenChange={setBorderOpen}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                        <span>Border</span>
                        {borderOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-3 pt-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-muted-foreground">Radius:</Label>
                          <NumberInputWithUnit
                            value={config.border_radius || '16px'}
                            onChange={(v) => {
                              updateField('border_radius', v);
                              updateField('template_id', null);
                            }}
                            units={['px', 'rem']}
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Subseção: Advanced */}
                    <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
                        <span>Avançado</span>
                        {advancedOpen ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-3 pt-2">
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground flex items-center gap-2">
                            <Code className="h-3 w-3" />
                            CSS Customizado
                          </Label>
                          <Textarea
                            value={config.custom_css || ''}
                            onChange={(e) => updateField('custom_css', e.target.value)}
                            placeholder="/* Seus estilos CSS aqui */"
                            className="font-mono text-xs min-h-[100px]"
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>

                  {/* Divisor */}
                  <div className="border-t" />

                  {/* Bot Avatar - toggle independente da URL */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Bot avatar</span>
                        <Switch
                          checked={config.bot_avatar_enabled || false}
                          onCheckedChange={(checked) => updateField('bot_avatar_enabled', checked)}
                          className="data-[state=checked]:bg-orange-500"
                        />
                      </div>
                      <Avatar className="h-10 w-10">
                        {config.bot_avatar_url ? (
                          <AvatarImage src={config.bot_avatar_url} />
                        ) : null}
                        <AvatarFallback 
                          style={{ 
                            backgroundColor: `${config.primary_color || (config.template_id === 'whatsapp' ? config.header_bg_color : '#5BBFBA')}20`,
                          }}
                        >
                          <Bot className="h-5 w-5" style={{ color: config.primary_color || (config.template_id === 'whatsapp' ? config.header_bg_color : '#5BBFBA') }} />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    {/* Input de URL só aparece quando toggle está ativado */}
                    {/* Input de URL do avatar com seleção via galeria */}
                    {config.bot_avatar_enabled && (
                      <MediaUrlInput
                        value={config.bot_avatar_url || null}
                        onChange={(url) => updateField('bot_avatar_url', url)}
                        placeholder="https://exemplo.com/avatar.png (opcional)"
                        showPreview={false}
                        allowedTypes={['image']}
                      />
                    )}
                  </div>

                  {/* User Avatar */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">User avatar</span>
                        <Switch
                          checked={config.user_avatar_enabled || false}
                          onCheckedChange={(checked) => updateField('user_avatar_enabled', checked)}
                          className="data-[state=checked]:bg-orange-500"
                        />
                      </div>
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-muted">
                          <User className="h-5 w-5 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* ========== SEÇÃO: Branding ========== */}
              <AccordionItem value="branding" className="border-b">
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Branding</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {/* Nome da empresa */}
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">Nome da empresa</Label>
                    <Input
                      value={config.company_name || ''}
                      onChange={(e) => updateField('company_name', e.target.value)}
                      placeholder="Sua Empresa"
                    />
                  </div>

                  {/* Logo URL - com seleção via galeria de mídia */}
                  <MediaUrlInput
                    value={config.company_logo_url || null}
                    onChange={(url) => updateField('company_logo_url', url)}
                    label="URL do logo"
                    placeholder="https://exemplo.com/logo.png"
                    showPreview={true}
                    allowedTypes={['image']}
                  />

                  {/* Powered by UazyChat - Removido para ocultar opção do cliente */}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </ScrollArea>
        </div>

        {/* Preview flutuante */}
        <div className="flex-1 bg-card rounded-xl shadow-sm overflow-hidden flex flex-col">
          {/* Header do preview */}
          <div className="px-4 py-2 border-b flex items-center justify-between bg-muted/30">
            <span className="text-sm text-muted-foreground">Preview</span>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
          
          {/* Área de preview */}
          <div className="flex-1 overflow-hidden">
            <ChatPreview config={config} />
          </div>
        </div>
      </div>
    </div>
  );
}
